/**
 * 374 (#1238): the customer photo windows the platform publishes (`photo_windows` on `GET /bookings` and
 * `GET /bookings/:id`, decided by `backend/src/modules/bookings/photoWindows.ts`). The admin shows these states and
 * never works a window out itself.
 */
export type PhotoWindowState = 'later' | 'open' | 'sent' | 'missed' | 'unavailable';

export interface PhotoWindow {
  state: PhotoWindowState;
  opens_at: string;
  closes_at: string;
  sent_at?: string;
  photo_count?: number;
  damage_count?: number;
}

export interface PhotoWindows {
  pickup: PhotoWindow;
  return: PhotoWindow;
  /** 415 (#1520): the fuel photo after the pickup set; absent from a payload older than 415. */
  fuel?: PhotoWindow;
}

/** The words staff read for each state, on the booking page. */
export const STATE_LABEL: Record<PhotoWindowState, string> = {
  sent: 'Sent',
  open: 'Open',
  later: 'Not yet',
  missed: 'Not sent',
  unavailable: 'Not available',
};

/** Badge classes per state (design tokens only). */
export const STATE_TONE: Record<PhotoWindowState, string> = {
  sent: 'bg-semantic-success-bg text-semantic-success-text',
  open: 'bg-brand-blue-soft text-primary-deep',
  later: 'bg-surface-feature text-steel',
  missed: 'bg-semantic-warning-bg text-semantic-warning-text',
  unavailable: 'bg-surface-feature text-steel',
};

const ZONE = 'Asia/Kuala_Lumpur';

/** A time staff read in Malaysia time, e.g. "Wed 8 Oct, 10:06 am". */
export function mytWhen(iso: string): string {
  return new Date(iso).toLocaleString('en-MY', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: ZONE,
  });
}

function duration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h} h ${m} min` : `${h} h`;
}

/** "6 min after pickup", "8 min before return", "at pickup". */
export function relativeTo(atIso: string, momentIso: string, word: 'pickup' | 'return'): string {
  const diff = Math.round((new Date(atIso).getTime() - new Date(momentIso).getTime()) / 60_000);
  if (diff === 0) return `at ${word}`;
  return `${duration(Math.abs(diff))} ${diff > 0 ? 'after' : 'before'} ${word}`;
}
